import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import teacherService from '../../services/api/teacher';
import { 
  Save, 
  ArrowRight, 
  Trash2, 
  Plus,
  AlertCircle,
  CheckCircle2,
  X,
  Award,
  Clock,
  Zap,
  Target,
  FileText,
  Eye,
  Settings,
  ShieldCheck,
  MousePointerClick,
  HelpCircle,
  Layers,
  Calendar
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';

const EditQuiz = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  // جلب تفاصيل الاختبار (المنطق الأصلي)
  const { data: quizData, isLoading, error } = useQuery(
    ['quiz-details', quizId],
    () => teacherService.getQuizDetails(quizId),
    { retry: 1 }
  );

  const quiz = useMemo(() => quizData?.quiz || {}, [quizData]);

  // حالة النموذج (Form State)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: 30,
    passingScore: 60,
    maxAttempts: 3,
    showCorrectAnswers: true,
    isPublished: false,
    isActive: true,
    questions: []
  });

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    questionType: 'multiple_choice',
    explanation: '',
    isRequired: true,
    answers: [
      { text: '', isCorrect: true, order: 1 },
      { text: '', isCorrect: false, order: 2 }
    ]
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // تحديث formData عند تحميل البيانات (المنطق الأصلي كاملاً)
  useEffect(() => {
    if (quiz.title) {
      setFormData({
        title: quiz.title,
        description: quiz.description || '',
        duration: quiz.timer_minutes || 30,
        passingScore: quiz.passing_score || 60,
        maxAttempts: quiz.max_attempts || 3,
        showCorrectAnswers: quiz.show_correct_answers !== false,
        isPublished: quiz.is_published || false,
        isActive: quiz.is_active !== false,
        questions: quiz.questions || []
      });
    }
  }, [quiz]);

  // Mutation لتحديث الاختبار (المنطق الأصلي)
  const updateQuizMutation = useMutation(
    async (quizData) => {
      const response = await teacherService.updateQuiz(quizId, quizData);
      return response;
    },
    {
      onSuccess: () => {
        toast.success('تم حفظ تعديلات الاختبار بنجاح!');
        queryClient.invalidateQueries(['quiz-details', quizId]);
        queryClient.invalidateQueries('teacher-quizzes');
        navigate('/teacher/quizzes');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في تحديث بيانات الاختبار');
        setIsSubmitting(false);
      }
    }
  );

  const handleAddQuestion = () => {
    setCurrentQuestionIndex(formData.questions.length);
    setQuestionForm({
      questionText: '',
      questionType: 'multiple_choice',
      explanation: '',
      isRequired: true,
      answers: [
        { text: '', isCorrect: true, order: 1 },
        { text: '', isCorrect: false, order: 2 }
      ]
    });
    setShowQuestionModal(true);
  };

  const handleEditQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setQuestionForm(formData.questions[index]);
    setShowQuestionModal(true);
  };

  const handleSaveQuestion = () => {
    if (!questionForm.questionText.trim()) {
      toast.error('يرجى كتابة نص السؤال أولاً');
      return;
    }

    const validAnswers = questionForm.answers.filter(a => a.text.trim());
    if (validAnswers.length < 2) {
      toast.error('يجب إضافة خيارين على الأقل للسؤال');
      return;
    }

    if (!validAnswers.some(a => a.isCorrect)) {
      toast.error('يرجى تحديد إجابة صحيحة واحدة');
      return;
    }

    const updatedQuestions = [...formData.questions];
    updatedQuestions[currentQuestionIndex] = {
      ...questionForm,
      answers: validAnswers.map((a, i) => ({ ...a, order: i + 1 })),
      order: currentQuestionIndex + 1
    };

    setFormData(prev => ({ ...prev, questions: updatedQuestions }));
    setShowQuestionModal(false);
    toast.success('تم تحديث السؤال في القائمة');
  };

  const handleDeleteQuestion = (index) => {
    if (window.confirm('هل أنت متأكد من حذف هذا السؤال نهائياً؟')) {
      setFormData(prev => ({
        ...prev,
        questions: prev.questions.filter((_, i) => i !== index)
      }));
      toast.success('تم حذف السؤال من الاختبار');
    }
  };

  const handleAddAnswer = () => {
    const newOrder = (questionForm.answers[questionForm.answers.length - 1]?.order || 0) + 1;
    setQuestionForm(prev => ({
      ...prev,
      answers: [...prev.answers, { text: '', isCorrect: false, order: newOrder }]
    }));
  };

  const handleUpdateAnswer = (index, field, value) => {
    const updatedAnswers = [...questionForm.answers];
    updatedAnswers[index] = { ...updatedAnswers[index], [field]: value };

    if (field === 'isCorrect' && value) {
      updatedAnswers.forEach((a, i) => { if (i !== index) a.isCorrect = false; });
    }

    setQuestionForm(prev => ({ ...prev, answers: updatedAnswers }));
  };

  const handleDeleteAnswer = (index) => {
    if (questionForm.answers.length > 2) {
      setQuestionForm(prev => ({
        ...prev,
        answers: prev.answers.filter((_, i) => i !== index)
      }));
    } else {
      toast.error('يجب أن يحتوي السؤال على خيارين على الأقل');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'عنوان الاختبار مطلوب';
    if (formData.duration < 1) newErrors.duration = 'المدة الزمنية غير صالحة';
    if (formData.passingScore < 0 || formData.passingScore > 100) newErrors.passingScore = 'درجة النجاح يجب أن تكون بين 0 و 100';
    if (formData.questions.length === 0) newErrors.questions = 'يجب إضافة سؤال واحد على الأقل لحفظ الاختبار';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
        toast.error('يرجى مراجعة البيانات والأسئلة');
        return;
    }
    setIsSubmitting(true);
    updateQuizMutation.mutate({
      title: formData.title,
      description: formData.description,
      duration: formData.duration,
      passingScore: formData.passingScore,
      maxAttempts: formData.maxAttempts,
      showCorrectAnswers: formData.showCorrectAnswers,
      isPublished: formData.isPublished,
      isActive: formData.isActive,
      questions: formData.questions
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تحميل بيانات الاختبار...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-6">
        <div className="bg-white rounded-[2.5rem] shadow-xl p-10 border border-red-100 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">فشل في تحميل الاختبار</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">نعتذر، حدث خطأ غير متوقع أثناء محاولة استرجاع بيانات الاختبار من الخادم.</p>
          <button
            onClick={() => navigate('/teacher/quizzes')}
            className="w-full px-8 py-3 bg-primary-600 text-white rounded-2xl font-bold hover:bg-primary-700 transition-all shadow-lg"
          >
            العودة لجميع الاختبارات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-10 px-4 sm:px-6 py-8 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden animate-in fade-in slide-in-from-top duration-500">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <button 
              onClick={() => navigate('/teacher/quizzes')}
              className="group flex items-center text-gray-400 hover:text-primary-600 font-bold transition-all mb-2 text-sm"
            >
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              قائمة الاختبارات
            </button>
            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
              <div className="bg-primary-600 text-white p-2 rounded-xl shadow-lg">
                <Award size={28} />
              </div>
              تعديل الاختبار
            </h1>
          </div>
          <div className="bg-primary-50 px-6 py-3 rounded-2xl border border-primary-100 text-[10px] font-black text-primary-600 uppercase tracking-widest flex items-center gap-2">
            <Calendar size={14} /> آخر تحديث: {new Date().toLocaleDateString('ar-EG')}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Main Content: Info & Questions (8 Columns) */}
        <div className="lg:col-span-8 space-y-8">
            
            {/* 1. Basic Information */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 space-y-8">
                <h2 className="text-xl font-black text-gray-900 flex items-center gap-3 uppercase tracking-tighter">
                    <FileText size={22} className="text-primary-600" /> المعلومات التعريفية
                </h2>

                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">عنوان الاختبار *</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            className={`w-full px-6 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 ${errors.title ? 'border-red-300' : 'border-gray-100'}`}
                        />
                        {errors.title && <p className="text-red-500 text-xs font-bold px-4">{errors.title}</p>}
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">الوصف والتعليمات</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows="4"
                            className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-medium text-gray-700 resize-none"
                            placeholder="اكتب تعليمات الطلاب هنا..."
                        />
                    </div>
                </div>
            </div>

            {/* 2. Questions List Builder */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 space-y-8">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-black text-gray-900 flex items-center gap-3 uppercase tracking-tighter">
                        <Zap size={22} className="text-primary-600" /> بنك الأسئلة
                    </h2>
                    <button
                        type="button"
                        onClick={handleAddQuestion}
                        className="bg-primary-50 text-primary-600 px-6 py-2.5 rounded-xl font-black text-xs hover:bg-primary-600 hover:text-white transition-all flex items-center gap-2 border border-primary-100"
                    >
                        <Plus size={16} /> إضافة سؤال جديد
                    </button>
                </div>

                {formData.questions.length === 0 ? (
                    <div className="py-20 text-center bg-gray-50/50 rounded-[2.5rem] border border-dashed border-gray-200">
                        <HelpCircle size={48} className="text-gray-200 mx-auto mb-4" />
                        <p className="text-gray-500 font-bold">لا توجد أسئلة مضافة حالياً.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {formData.questions.map((question, index) => (
                            <div key={index} className="group bg-white border border-gray-100 rounded-[2rem] p-6 hover:shadow-xl hover:border-primary-100 transition-all duration-300 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-1.5 h-full bg-primary-600 opacity-10 group-hover:opacity-100 transition-opacity"></div>
                                <div className="flex items-start justify-between gap-6">
                                    <div className="space-y-3 flex-1">
                                        <div className="flex items-center gap-3">
                                            <span className="bg-gray-900 text-white w-8 h-8 flex items-center justify-center rounded-xl font-black text-xs">
                                                {index + 1}
                                            </span>
                                            {question.isRequired && (
                                                <span className="bg-rose-50 text-rose-600 px-3 py-1 rounded-lg text-[9px] font-black uppercase border border-rose-100">سؤال إلزامي</span>
                                            )}
                                        </div>
                                        <p className="text-lg font-bold text-gray-800 leading-relaxed">{question.questionText}</p>
                                        
                                        <div className="flex flex-wrap gap-2 pt-2">
                                            {question.answers?.map((ans, idx) => (
                                                <div key={idx} className={`px-4 py-2 rounded-xl text-[10px] font-bold border transition-all ${ans.isCorrect ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-400 border-gray-100'}`}>
                                                    {ans.isCorrect && '✓ '}{ans.text}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="flex flex-col gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleEditQuestion(index)}
                                            className="w-11 h-11 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                                        >
                                            <Edit size={18} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteQuestion(index)}
                                            className="w-11 h-11 bg-red-50 text-red-600 rounded-xl flex items-center justify-center hover:bg-red-600 hover:text-white transition-all shadow-sm"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {errors.questions && <p className="text-rose-600 text-xs font-black uppercase text-center mt-4 tracking-widest">⚠️ {errors.questions}</p>}
            </div>
        </div>

        {/* Sidebar: Settings (4 Columns) */}
        <div className="lg:col-span-4 space-y-6">
            
            {/* 3. Global Quiz Settings */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
                <h3 className="text-lg font-black text-gray-900 mb-8 flex items-center gap-3 uppercase tracking-tighter">
                    <Settings size={20} className="text-primary-600" /> إعدادات الاختبار
                </h3>

                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">مدة الاختبار (دقائق)</label>
                        <div className="relative group">
                            <input
                                type="number"
                                value={formData.duration}
                                onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-black text-gray-900"
                            />
                            <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">نسبة النجاح (%)</label>
                        <div className="relative group">
                            <input
                                type="number"
                                value={formData.passingScore}
                                onChange={(e) => setFormData(prev => ({ ...prev, passingScore: parseInt(e.target.value) }))}
                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-black text-gray-900"
                            />
                            <Target className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">أقصى عدد محاولات</label>
                        <div className="relative group">
                            <input
                                type="number"
                                value={formData.maxAttempts}
                                onChange={(e) => setFormData(prev => ({ ...prev, maxAttempts: parseInt(e.target.value) }))}
                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-black text-gray-900"
                            />
                            <Layers className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                        </div>
                    </div>

                    <div className="pt-6 border-t border-gray-50 space-y-4">
                        {[
                            { id: 'showCorrectAnswers', label: 'كشف الإجابات بعد الحل', icon: Eye },
                            { id: 'isActive', label: 'الاختبار نشط ومتاح', icon: Zap },
                            { id: 'isPublished', label: 'نشر الاختبار للطلاب', icon: CheckCircle2 }
                        ].map((toggle) => (
                            <div key={toggle.id} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-gray-100 group transition-all hover:bg-white">
                                <div className="flex items-center gap-3">
                                    <toggle.icon size={16} className="text-primary-500" />
                                    <span className="text-xs font-bold text-gray-700">{toggle.label}</span>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData[toggle.id]}
                                        onChange={(e) => setFormData(prev => ({ ...prev, [toggle.id]: e.target.checked }))}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 shadow-inner"></div>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 4. Action Hub */}
            <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
                <div className="relative z-10 space-y-8 text-center">
                    <div className="bg-primary-600 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto shadow-xl shadow-primary-900/50 group-hover:rotate-12 transition-transform">
                        <Save size={28} />
                    </div>
                    <div className="space-y-2 text-center">
                        <h4 className="text-2xl font-black tracking-tight leading-tight">تحديث البيانات</h4>
                        <p className="text-gray-400 text-sm font-medium leading-relaxed">
                            احفظ كافة التغييرات التي أجريتها على الاختبار والأسئلة لضمان وصولها للطلاب.
                        </p>
                    </div>
                    <div className="space-y-4">
                        <button
                            type="submit"
                            disabled={isSubmitting || updateQuizMutation.isLoading}
                            className="w-full bg-white text-gray-900 py-5 rounded-[1.8rem] font-black text-lg hover:bg-primary-50 transition-all transform active:scale-95 shadow-xl flex items-center justify-center gap-3 disabled:opacity-50"
                        >
                            {isSubmitting ? <LoadingSpinner size="small" /> : <ShieldCheck size={24} />}
                            حفظ التعديلات
                        </button>
                        <button 
                            type="button"
                            onClick={() => navigate('/teacher/quizzes')}
                            className="text-xs font-bold text-gray-500 uppercase tracking-[0.2em] hover:text-white transition-colors"
                        >
                            إلغاء والعودة للقائمة
                        </button>
                    </div>
                </div>
            </div>
        </div>
      </form>

      {/* Modern Question Modal */}
      {showQuestionModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[300] p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full relative overflow-hidden transition-all transform scale-100">
            
            {/* Modal Header */}
            <div className="p-8 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary-600 rounded-2xl flex items-center justify-center text-white shadow-xl">
                    <HelpCircle size={24} />
                </div>
                <div>
                    <h3 className="text-xl font-black text-gray-900 leading-none uppercase tracking-tighter">
                        {currentQuestionIndex < formData.questions.length ? 'تعديل بيانات السؤال' : 'إضافة سؤال جديد'}
                    </h3>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1.5">تحرير بنية السؤال والخيارات</p>
                </div>
              </div>
              <button onClick={() => setShowQuestionModal(false)} className="w-10 h-10 flex items-center justify-center bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all border border-gray-100 shadow-sm">
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-8 lg:p-10 space-y-8 max-h-[65vh] overflow-y-auto scrollbar-thin">
              
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">نص السؤال *</label>
                <textarea
                  value={questionForm.questionText}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, questionText: e.target.value }))}
                  rows="3"
                  className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 resize-none leading-relaxed"
                  placeholder="اكتب السؤال بوضوح هنا..."
                />
              </div>

              {/* Answers Builder Section */}
              <div className="space-y-6 pt-6 border-t border-gray-50">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-black text-gray-900 tracking-tighter">خيارات الإجابة والتحقق</h4>
                  <button
                    type="button"
                    onClick={handleAddAnswer}
                    className="text-primary-600 font-black text-[10px] uppercase tracking-widest bg-primary-50 px-4 py-2 rounded-xl hover:bg-primary-600 hover:text-white transition-all"
                  >
                    + إضافة خيار
                  </button>
                </div>

                <div className="space-y-3">
                    {questionForm.answers.map((answer, index) => (
                    <div key={index} className={`flex items-center gap-4 p-4 rounded-3xl border-2 transition-all group ${answer.isCorrect ? 'border-emerald-500 bg-emerald-50/30' : 'border-gray-50 bg-gray-50/30 hover:border-gray-200'}`}>
                        <button
                            type="button"
                            onClick={() => handleUpdateAnswer(index, 'isCorrect', true)}
                            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all shadow-sm ${answer.isCorrect ? 'bg-emerald-600 text-white' : 'bg-white text-gray-300 hover:text-emerald-500'}`}
                        >
                            {answer.isCorrect ? <CheckCircle2 size={24} /> : <MousePointerClick size={20} />}
                        </button>
                        
                        <input
                            type="text"
                            value={answer.text}
                            onChange={(e) => handleUpdateAnswer(index, 'text', e.target.value)}
                            className="flex-1 bg-transparent border-none font-bold text-gray-800 focus:ring-0 placeholder:text-gray-300"
                            placeholder={`الخيار ${String.fromCharCode(65 + index)}`}
                        />

                        {questionForm.answers.length > 2 && (
                        <button
                            type="button"
                            onClick={() => handleDeleteAnswer(index)}
                            className="p-2 text-gray-300 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                        >
                            <Trash2 size={18} />
                        </button>
                        )}
                    </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-8 border-t border-gray-100 bg-gray-50/50 grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setShowQuestionModal(false)}
                className="px-6 py-4 border border-gray-200 rounded-2xl text-gray-400 font-black text-sm hover:bg-gray-50 transition-all uppercase tracking-widest"
              >
                تجاهل
              </button>
              <button
                type="button"
                onClick={handleSaveQuestion}
                className="px-6 py-4 bg-primary-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-2 transform active:scale-95"
              >
                <Save size={18} /> حفظ التعديلات
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// أيقونة إضافية
const Edit = ({ size, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);


export default EditQuiz;